import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ServerStackIcon,
  UserGroupIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  listOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  deactivateOrganization,
  listOrganizationUsers,
  assignUserToOrganization,
  removeUserFromOrganization,
  validateOrganization,
} from '../services/hcsOrganizationService';

/**
 * HCS Organizations Manager
 *
 * Administrative interface for managing HCS organizations and user assignments.
 * Part of UC-016: HCS Organization Management
 */
const HcsOrganizationsManager = () => {
  // Organizations state
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgsTotal, setOrgsTotal] = useState(0);
  const [orgsPagination, setOrgsPagination] = useState({
    limit: 25,
    offset: 0,
  });
  const [orgsFilter, setOrgsFilter] = useState({
    status: '',
    search: '',
  });

  // Selected organization state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Organization users state
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Validation state
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    org_id: '',
    org_name: '',
    database_name: '',
    warehouse_name: '',
    role_name: '',
    azure_container: '',
    notes: '',
  });
  const [assignData, setAssignData] = useState({
    user_id: '',
    is_primary: false,
    access_level: 'standard',
  });

  // Loading/saving states
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Admin email for audit (in production, get from auth context)
  const adminEmail = 'admin@videxa.co';

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch organizations on mount and when filters change
  useEffect(() => {
    fetchOrganizations();
  }, [orgsPagination, orgsFilter.status]);

  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const result = await listOrganizations({
        status: orgsFilter.status || undefined,
        search: orgsFilter.search || undefined,
        limit: orgsPagination.limit,
        offset: orgsPagination.offset,
      });
      setOrganizations(result.organizations || []);
      setOrgsTotal(result.total || 0);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load organizations' });
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setOrgsPagination((prev) => ({ ...prev, offset: 0 }));
    fetchOrganizations();
  };

  const handleSelectOrg = async (org) => {
    setSelectedOrg(org);
    setLoadingDetail(true);
    setValidationResult(null);

    try {
      const detail = await getOrganization(org.org_id);
      setOrgDetail(detail);
      // Also fetch users for this org
      fetchOrgUsers(org.org_id);
    } catch (error) {
      console.error('Error fetching organization details:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load organization details' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchOrgUsers = async (orgId) => {
    setLoadingUsers(true);
    try {
      const result = await listOrganizationUsers(orgId);
      setOrgUsers(result.assignments || []);
    } catch (error) {
      console.error('Error fetching organization users:', error);
      setOrgUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleValidateOrg = async () => {
    if (!selectedOrg) return;
    setValidating(true);
    setValidationResult(null);

    try {
      const result = await validateOrganization(selectedOrg.org_id);
      setValidationResult(result);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await createOrganization(formData, adminEmail);
      setMessage({ type: 'success', text: 'Organization created successfully' });
      setShowCreateModal(false);
      resetForm();
      fetchOrganizations();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to create organization' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setSaving(true);

    try {
      const updates = {
        org_name: formData.org_name,
        database_name: formData.database_name,
        warehouse_name: formData.warehouse_name,
        role_name: formData.role_name,
        azure_container: formData.azure_container,
        notes: formData.notes,
      };
      const updated = await updateOrganization(selectedOrg.org_id, updates, adminEmail);
      setMessage({ type: 'success', text: 'Organization updated successfully' });
      setShowEditModal(false);
      setOrgDetail(updated);
      fetchOrganizations();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update organization' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateOrg = async () => {
    if (!selectedOrg) return;
    setSaving(true);

    try {
      await deactivateOrganization(selectedOrg.org_id, adminEmail);
      setMessage({ type: 'success', text: 'Organization deactivated successfully' });
      setShowDeactivateConfirm(false);
      setSelectedOrg(null);
      setOrgDetail(null);
      fetchOrganizations();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to deactivate organization' });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setSaving(true);

    try {
      await assignUserToOrganization(selectedOrg.org_id, assignData, adminEmail);
      setMessage({ type: 'success', text: 'User assigned to organization' });
      setShowAssignModal(false);
      setAssignData({ user_id: '', is_primary: false, access_level: 'standard' });
      fetchOrgUsers(selectedOrg.org_id);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to assign user' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedOrg) return;
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;

    try {
      await removeUserFromOrganization(selectedOrg.org_id, userId, adminEmail);
      setMessage({ type: 'success', text: 'User removed from organization' });
      fetchOrgUsers(selectedOrg.org_id);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to remove user' });
    }
  };

  const resetForm = () => {
    setFormData({
      org_id: '',
      org_name: '',
      database_name: '',
      warehouse_name: '',
      role_name: '',
      azure_container: '',
      notes: '',
    });
  };

  const openEditModal = () => {
    if (orgDetail) {
      setFormData({
        org_id: orgDetail.org_id,
        org_name: orgDetail.org_name,
        database_name: orgDetail.database_name,
        warehouse_name: orgDetail.warehouse_name,
        role_name: orgDetail.role_name,
        azure_container: orgDetail.azure_container || '',
        notes: orgDetail.notes || '',
      });
      setShowEditModal(true);
    }
  };

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Message Banner */}
      {message.text && (
        <div
          className={`border-l-4 p-4 rounded-r-md ${
            message.type === 'error'
              ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/20'
              : message.type === 'success'
              ? 'bg-green-50 border-green-500 text-green-700 dark:bg-green-900/20'
              : 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {message.type === 'error' && <ExclamationTriangleIcon className="h-5 w-5 mr-2" />}
              {message.type === 'success' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Organizations List */}
        <div className="lg:col-span-1 space-y-6">
          {/* Header & Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <BuildingOffice2Icon className="h-5 w-5 mr-2 text-[#FFC000]" />
                HCS Organizations
              </h2>
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="px-3 py-1.5 bg-[#FFC000] text-[#0E3056] rounded-md text-sm font-medium hover:bg-[#e6ad00] flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>

            {/* Search & Filter */}
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={orgsFilter.search}
                  onChange={(e) => setOrgsFilter({ ...orgsFilter, search: e.target.value })}
                  placeholder="Search by ID or name..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="submit"
                  className="px-3 py-2 bg-[#0E3056] text-white rounded-md hover:bg-[#1a4a7a]"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                </button>
              </div>
              <select
                value={orgsFilter.status}
                onChange={(e) => {
                  setOrgsFilter({ ...orgsFilter, status: e.target.value });
                  setOrgsPagination((prev) => ({ ...prev, offset: 0 }));
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </form>

            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Total: <span className="font-semibold text-[#FFC000]">{orgsTotal}</span> organizations
            </div>
          </div>

          {/* Organizations List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            {loadingOrgs ? (
              <div className="p-8 text-center">
                <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-500">Loading organizations...</p>
              </div>
            ) : organizations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No organizations found</div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {organizations.map((org) => (
                  <button
                    key={org.org_id}
                    onClick={() => handleSelectOrg(org)}
                    className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedOrg?.org_id === org.org_id
                        ? 'bg-[#FFC000] bg-opacity-10 border-l-4 border-[#FFC000]'
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{org.org_id}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{org.org_name}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(
                          org.status
                        )}`}
                      >
                        {org.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Pagination */}
            {orgsTotal > orgsPagination.limit && (
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {orgsPagination.offset + 1}-
                    {Math.min(orgsPagination.offset + orgsPagination.limit, orgsTotal)} of {orgsTotal}
                  </span>
                  <div className="flex space-x-1">
                    <button
                      onClick={() =>
                        setOrgsPagination((prev) => ({
                          ...prev,
                          offset: Math.max(0, prev.offset - prev.limit),
                        }))
                      }
                      disabled={orgsPagination.offset === 0}
                      className="p-1 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        setOrgsPagination((prev) => ({ ...prev, offset: prev.offset + prev.limit }))
                      }
                      disabled={orgsPagination.offset + orgsPagination.limit >= orgsTotal}
                      className="p-1 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Organization Details */}
        <div className="lg:col-span-2">
          {selectedOrg ? (
            <div className="space-y-6">
              {/* Organization Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-14 h-14 bg-[#0E3056] rounded-lg flex items-center justify-center">
                        <BuildingOffice2Icon className="h-7 w-7 text-white" />
                      </div>
                      <div className="ml-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                          {orgDetail?.org_name || selectedOrg.org_name}
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 font-mono">
                          {selectedOrg.org_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={openEditModal}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center text-sm"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                      {orgDetail?.status === 'active' && (
                        <button
                          onClick={() => setShowDeactivateConfirm(true)}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center text-sm"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          Deactivate
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {loadingDetail ? (
                  <div className="p-8 text-center">
                    <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Loading details...</p>
                  </div>
                ) : orgDetail ? (
                  <div className="p-6 space-y-6">
                    {/* Status & Quick Info */}
                    <div className="flex items-center space-x-4">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClasses(
                          orgDetail.status
                        )}`}
                      >
                        {orgDetail.status}
                      </span>
                      <button
                        onClick={handleValidateOrg}
                        disabled={validating}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                      >
                        <ShieldCheckIcon className="h-4 w-4 mr-1" />
                        {validating ? 'Validating...' : 'Validate Resources'}
                      </button>
                    </div>

                    {/* Validation Result */}
                    {validationResult && (
                      <div
                        className={`p-4 rounded-md ${
                          validationResult.all_valid
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                        }`}
                      >
                        <h4 className="font-medium mb-2 flex items-center">
                          {validationResult.all_valid ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                              All Resources Valid
                            </>
                          ) : (
                            <>
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                              Some Resources Missing
                            </>
                          )}
                        </h4>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="flex items-center">
                            <span
                              className={
                                validationResult.validation?.database_exists
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }
                            >
                              Database:{' '}
                              {validationResult.validation?.database_exists ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span
                              className={
                                validationResult.validation?.warehouse_exists
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }
                            >
                              Warehouse:{' '}
                              {validationResult.validation?.warehouse_exists ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span
                              className={
                                validationResult.validation?.role_exists
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }
                            >
                              Role: {validationResult.validation?.role_exists ? '✓' : '✗'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Snowflake Resources */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <ServerStackIcon className="h-4 w-4 mr-2 text-[#FFC000]" />
                        Snowflake Resources
                      </h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Database
                          </p>
                          <p className="font-mono text-sm text-gray-900 dark:text-white">
                            {orgDetail.database_name}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Warehouse
                          </p>
                          <p className="font-mono text-sm text-gray-900 dark:text-white">
                            {orgDetail.warehouse_name}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Role
                          </p>
                          <p className="font-mono text-sm text-gray-900 dark:text-white">
                            {orgDetail.role_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Azure Storage */}
                    {orgDetail.azure_container && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          Azure Storage
                        </h3>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Container
                          </p>
                          <p className="font-mono text-sm text-gray-900 dark:text-white">
                            {orgDetail.azure_container}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {orgDetail.notes && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          Notes
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {orgDetail.notes}
                        </p>
                      </div>
                    )}

                    {/* Audit Info */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p>
                        Created: {orgDetail.created_at ? new Date(orgDetail.created_at).toLocaleString() : '-'}
                        {orgDetail.created_by && ` by ${orgDetail.created_by}`}
                      </p>
                      {orgDetail.updated_at && (
                        <p>
                          Updated: {new Date(orgDetail.updated_at).toLocaleString()}
                          {orgDetail.updated_by && ` by ${orgDetail.updated_by}`}
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Assigned Users Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                      Assigned Users ({orgUsers.length})
                    </h3>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="px-3 py-1.5 bg-[#0E3056] text-white rounded-md text-sm font-medium hover:bg-[#1a4a7a] flex items-center"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-1" />
                      Assign User
                    </button>
                  </div>
                </div>

                {loadingUsers ? (
                  <div className="p-8 text-center">
                    <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                  </div>
                ) : orgUsers.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No users assigned to this organization
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {orgUsers.map((assignment) => (
                      <div
                        key={assignment.assignment_id}
                        className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                              {assignment.email?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {assignment.email || assignment.user_id}
                            </p>
                            <div className="flex items-center space-x-2 text-xs">
                              <span
                                className={`px-2 py-0.5 rounded ${
                                  assignment.access_level === 'admin'
                                    ? 'bg-purple-100 text-purple-800'
                                    : assignment.access_level === 'readonly'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {assignment.access_level}
                              </span>
                              {assignment.is_primary && (
                                <span className="px-2 py-0.5 rounded bg-[#FFC000] text-[#0E3056]">
                                  Primary
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveUser(assignment.user_id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                          title="Remove from organization"
                        >
                          <UserMinusIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
              <BuildingOffice2Icon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                Select an Organization
              </h2>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Choose an organization from the list to view details and manage users
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowCreateModal(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto z-10">
              <form onSubmit={handleCreateOrg}>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Create New Organization
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization ID *
                      </label>
                      <input
                        type="text"
                        value={formData.org_id}
                        onChange={(e) =>
                          setFormData({ ...formData, org_id: e.target.value.toUpperCase() })
                        }
                        placeholder="HCS0001"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        value={formData.org_name}
                        onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                        placeholder="Healthcare System Name"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Database *
                      </label>
                      <input
                        type="text"
                        value={formData.database_name}
                        onChange={(e) =>
                          setFormData({ ...formData, database_name: e.target.value.toUpperCase() })
                        }
                        placeholder="HCS0001_DB"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Warehouse *
                      </label>
                      <input
                        type="text"
                        value={formData.warehouse_name}
                        onChange={(e) =>
                          setFormData({ ...formData, warehouse_name: e.target.value.toUpperCase() })
                        }
                        placeholder="HCS0001_WH"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Role *
                      </label>
                      <input
                        type="text"
                        value={formData.role_name}
                        onChange={(e) =>
                          setFormData({ ...formData, role_name: e.target.value.toUpperCase() })
                        }
                        placeholder="HCS0001_ROLE"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Azure Container (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.azure_container}
                      onChange={(e) =>
                        setFormData({ ...formData, azure_container: e.target.value })
                      }
                      placeholder="hcs-00001"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-[#FFC000] text-[#0E3056] rounded-md font-semibold hover:bg-[#e6ad00] disabled:opacity-50"
                  >
                    {saving ? 'Creating...' : 'Create Organization'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowEditModal(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto z-10">
              <form onSubmit={handleUpdateOrg}>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Edit Organization: {formData.org_id}
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.org_name}
                      onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Database *
                      </label>
                      <input
                        type="text"
                        value={formData.database_name}
                        onChange={(e) =>
                          setFormData({ ...formData, database_name: e.target.value.toUpperCase() })
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Warehouse *
                      </label>
                      <input
                        type="text"
                        value={formData.warehouse_name}
                        onChange={(e) =>
                          setFormData({ ...formData, warehouse_name: e.target.value.toUpperCase() })
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Role *
                      </label>
                      <input
                        type="text"
                        value={formData.role_name}
                        onChange={(e) =>
                          setFormData({ ...formData, role_name: e.target.value.toUpperCase() })
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Azure Container
                    </label>
                    <input
                      type="text"
                      value={formData.azure_container}
                      onChange={(e) =>
                        setFormData({ ...formData, azure_container: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-[#FFC000] text-[#0E3056] rounded-md font-semibold hover:bg-[#e6ad00] disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign User Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAssignModal(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto z-10">
              <form onSubmit={handleAssignUser}>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Assign User to {selectedOrg?.org_id}
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      User ID *
                    </label>
                    <input
                      type="text"
                      value={assignData.user_id}
                      onChange={(e) => setAssignData({ ...assignData, user_id: e.target.value })}
                      placeholder="Enter user UUID"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the user's UUID from the Users tab
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Access Level
                    </label>
                    <select
                      value={assignData.access_level}
                      onChange={(e) =>
                        setAssignData({ ...assignData, access_level: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    >
                      <option value="standard">Standard</option>
                      <option value="admin">Admin</option>
                      <option value="readonly">Read Only</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_primary"
                      checked={assignData.is_primary}
                      onChange={(e) =>
                        setAssignData({ ...assignData, is_primary: e.target.checked })
                      }
                      className="h-4 w-4 text-[#FFC000] border-gray-300 rounded focus:ring-[#FFC000]"
                    />
                    <label
                      htmlFor="is_primary"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      Set as user's primary organization
                    </label>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-[#0E3056] text-white rounded-md font-semibold hover:bg-[#1a4a7a] disabled:opacity-50"
                  >
                    {saving ? 'Assigning...' : 'Assign User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {showDeactivateConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowDeactivateConfirm(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto z-10">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                  Deactivate Organization?
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                  Are you sure you want to deactivate <strong>{selectedOrg?.org_id}</strong>? Users
                  will no longer be able to access this organization's resources.
                </p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => setShowDeactivateConfirm(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeactivateOrg}
                    disabled={saving}
                    className="px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    {saving ? 'Deactivating...' : 'Yes, Deactivate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HcsOrganizationsManager;
